import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Settings → Easeedesk → Chat settings si Languages.
 *
 * Perechile vechi: `chat_settings`, `get_chat_general_settings`,
 * `save_chat_general_settings`, `get_chat_tickets_settings`,
 * `save_chat_tickets_settings`, `chat_languages_messages`, `messages_list`,
 * `message_variables`, `create_message`, `update_message`, `remove_message`,
 * `change_message_active`.
 *
 * `apiGet` / `apiPost` pun ELE `/api/v2` in fata caii.
 * `company = 0` inseamna "toate firmele", ca in portal.
 */

export interface ChatShell {
  title: string
  moduleUrl: string
  companies: Array<{ ID: number; name: string }>
  sources: Array<{ chat_source: number; source_name: string }>
}

export interface ChatGeneralSettings {
  chat_title: string
  button_color: string
  received_message_color: string
  sent_message_color: string
  automated_user: string
  user_notifications: number
  user_notifications_time: number
  open_chat_window: number
  open_chat_window_consent: number
}

export interface ChatGeneral {
  company: number
  settings: ChatGeneralSettings
  /** Sablonul e numai al unei FIRME; ceasul de instiintare numai al furnizorului. */
  has_template: boolean
  template: string
  has_user_notifications: boolean
  templates: string[]
}

export interface ChatTicketSetting {
  setting_id: number
  setting_name: string
  info: string
  /** `ticket_types`, `ticket_statuses`, `ticket_priorities`, `groups` sau null. */
  select_field: string | null
  select_value: number | null
  checked: number
}

export interface ChatTickets {
  company: number
  chat_source: number
  settings: ChatTicketSetting[]
  ticket_types: Array<{ ID: number; name: string }>
  ticket_statuses: Array<{ ID: number; name: string }>
  ticket_priorities: Array<{ ID: number; name: string }>
  groups: Array<{ ID: number; name: string }>
}

export interface ChatMessagesShell {
  title: string
  moduleUrl: string
  companies: Array<{ ID: number; name: string }>
  languages: Array<{ id: number; language_name: string; short_name: string }>
  messages_type: Array<{ id: number; type_name: string }>
}

export interface ChatMessage {
  id: number
  company_id: number
  language_id: number
  message_type: number
  message: string
  message_delay: number
  active: number
}

const BASE = '/settings/easeedesk/chat_settings'
const MESSAGES = '/settings/easeedesk/chat_languages_messages'

export function chatShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'chat_settings'],
    queryFn: () => apiGet<ChatShell>(BASE),
  })
}

export function chatGeneralQuery(company: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'chat_settings', 'general', company],
    queryFn: () => apiGet<ChatGeneral>(`${BASE}/general?company=${company}`),
  })
}

export function saveChatGeneral(company: number, settings: ChatGeneralSettings, template: string) {
  return apiPost<{ ok?: boolean }>(`${BASE}/general`, {
    company,
    'settings[chat_title]': settings.chat_title,
    'settings[button_color]': settings.button_color,
    'settings[received_message_color]': settings.received_message_color,
    'settings[sent_message_color]': settings.sent_message_color,
    'settings[automated_user]': settings.automated_user,
    'settings[user_notifications]': settings.user_notifications,
    'settings[user_notifications_time]': settings.user_notifications_time,
    'settings[open_chat_window]': settings.open_chat_window,
    'settings[open_chat_window_consent]': settings.open_chat_window_consent,
    'settings[template]': template,
  })
}

export function chatTicketsQuery(company: number, chatSource: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'chat_settings', 'tickets', company, chatSource],
    queryFn: () => apiGet<ChatTickets>(`${BASE}/tickets?company=${company}&chat_source=${chatSource}`),
  })
}

/**
 * Starile tipului ales in setarile de tichet, din fluxul lui -
 * `get_ticket_type_status`, cu care vechea macheta inlocuia `ticket_statuses`
 * (starile implicite ale furnizorului) la fiecare schimbare a tipului.
 */
export function chatTicketTypeStatusesQuery(type: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'chat_settings', 'ticket_type_statuses', type],
    queryFn: () => apiGet<{ statuses: Array<{ ID: number; name: string }> }>(`${BASE}/tickets/types/${type}/statuses`),
    enabled: type > 0,
  })
}

export function saveChatTickets(
  company: number,
  chatSource: number,
  rows: Array<{ setting_id: number; value: number; checked: boolean }>,
) {
  const fields: Record<string, string | number> = { company, chat_source: chatSource }
  for (const row of rows) {
    fields[`settings[${row.setting_id}][value]`] = row.value
    fields[`settings[${row.setting_id}][checked]`] = row.checked ? 1 : 0
  }
  return apiPost<{ ok?: boolean }>(`${BASE}/tickets`, fields)
}

export function chatMessagesShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'chat_languages_messages'],
    queryFn: () => apiGet<ChatMessagesShell>(MESSAGES),
  })
}

export function chatMessagesQuery(company: number, language: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'chat_languages_messages', 'list', company, language],
    queryFn: () =>
      apiGet<{
        messages: ChatMessage[]
        message_active: Array<{ id: number; type_name: string; active: number }>
      }>(`${MESSAGES}/list?company=${company}&language=${language}`),
    enabled: language > 0,
  })
}

export function chatMessageVariablesQuery(type: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'chat_languages_messages', 'variables', type],
    queryFn: () => apiGet<{ variables: Record<string, string> }>(`${MESSAGES}/variables?type=${type}`),
    enabled: type > 0,
  })
}

export interface ChatMessageForm {
  company: number
  language: number
  message_type: number
  message_body: string
  message_delay: number
}

export function createChatMessage(form: ChatMessageForm) {
  return apiPost<{ ok?: boolean; id?: number }>(MESSAGES, { ...form })
}

export function updateChatMessage(message: number, form: ChatMessageForm) {
  return apiPost<{ ok?: boolean }>(`${MESSAGES}/${message}`, { ...form })
}

export function removeChatMessage(message: number) {
  return apiPost<{ ok?: boolean }>(`${MESSAGES}/${message}/remove`, {})
}

export function setChatMessagesActive(messageType: number, language: number, active: boolean) {
  return apiPost<{ ok?: boolean; active?: number }>(`${MESSAGES}/active`, {
    message_type_id: messageType,
    language_id: language,
    message_active: active ? 1 : 0,
  })
}

/**
 * Butonul "Refresh cron" din setarile generale ale furnizorului.
 *
 * Perechea lui `rebuild_chat_configuration`. POST, nu GET ca in portal: cere
 * serverului de cronuri sa porneasca sau sa opreasca ceva, deci schimba o
 * stare. Raspunsul spune si daca butonul a ajuns chiar la celalalt capat.
 */
export function rebuildChatConfiguration() {
  return apiPost<{ result: string; action: string; cron: string; message?: string }>(`${BASE}/rebuild_configuration`, {})
}
